import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useActorEmails, fmtAuthor } from "@/hooks/use-actor-emails";
import { optimizeImage } from "@/lib/image-optim";

export const Route = createFileRoute("/_authenticated/admin/contenidos")({
  
  head: () => ({ meta: [{ title: "Admin — Contenidos | Mine Class" }, { name: "description", content: "Panel de administración de Contenidos — acceso restringido a Mine Class." }, { name: "robots", content: "noindex, nofollow" }] }),
  component: ContenidosUnified,
});

type Section = "noticias" | "newsletter" | "multimedia" | "podcast" | "documentos";

const SECTIONS: { key: Section; label: string; desc: string; icon: string }[] = [
  { key: "noticias", label: "Noticia", desc: "Título, bajada, cuerpo, SEO e imagen 1200×630", icon: "📰" },
  { key: "newsletter", label: "Newsletter", desc: "Pega el HTML exportado desde HubSpot", icon: "✉️" },
  { key: "multimedia", label: "Multimedia", desc: "Fotos y videos organizados en carpetas", icon: "🖼️" },
  { key: "podcast", label: "Podcast", desc: "Capítulo con embeds de Spotify o YouTube", icon: "🎙️" },
  { key: "documentos", label: "Documento", desc: "Título, área y archivo descargable", icon: "📄" },
];

function ContenidosUnified() {
  const [section, setSection] = useState<Section | null>(null);

  if (!section) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-[var(--color-brand-purple)]">Subir contenido</h2>
          <p className="text-sm text-[var(--color-on-surface-variant)] mt-1">
            Elige la sección donde quieres publicar. Todo lo que subas aquí aparece automáticamente en la web.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              className="text-left bg-white border-2 border-[var(--color-outline-variant)] hover:border-[var(--color-brand-coral)] p-6 transition-colors"
            >
              <div className="text-4xl mb-3">{s.icon}</div>
              <h3 className="font-bold text-lg text-[var(--color-brand-purple)]">{s.label}</h3>
              <p className="text-xs text-[var(--color-on-surface-variant)] mt-1">{s.desc}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const current = SECTIONS.find((x) => x.key === section)!;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => setSection(null)} className="text-sm underline text-[var(--color-brand-purple)]">
          ← Cambiar sección
        </button>
        <h2 className="text-xl font-bold">
          {current.icon} {current.label}
        </h2>
      </div>
      {section === "noticias" && <NoticiasPanel />}
      {section === "newsletter" && <NewsletterPanel />}
      {section === "multimedia" && <MultimediaPanel />}
      {section === "podcast" && <PodcastPanel />}
      {section === "documentos" && <DocumentosPanel />}
    </div>
  );
}

/* ---------------- NOTICIAS ---------------- */

type Noticia = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body_md: string | null;
  category: string | null;
  is_published: boolean;
  published_at: string | null;
  seo_title: string | null;
  seo_description: string | null;
  og_image_url: string | null;
  created_at: string;
  author_id: string | null;
  updated_by: string | null;
};

function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);
}

function NoticiasPanel() {
  const [items, setItems] = useState<Noticia[]>([]);
  const [editing, setEditing] = useState<Partial<Noticia> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const inlineImgRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [inlineImgBusy, setInlineImgBusy] = useState(false);
  const lookup = useActorEmails(items.flatMap((n) => [n.author_id, n.updated_by]));

  function applyToSelection(fn: (sel: string) => { text: string; caretOffset?: number }) {
    const ta = bodyRef.current;
    const cur = editing?.body_md ?? "";
    if (!ta || !editing) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = cur.slice(start, end);
    const { text, caretOffset } = fn(sel);
    const next = cur.slice(0, start) + text + cur.slice(end);
    setEditing({ ...editing, body_md: next });
    setTimeout(() => {
      ta.focus();
      const pos = start + (caretOffset ?? text.length);
      ta.selectionStart = ta.selectionEnd = pos;
    }, 0);
  }
  function insertAtCursor(text: string) { applyToSelection(() => ({ text })); }
  function wrap(before: string, after = before, placeholder = "texto") {
    applyToSelection((sel) => {
      const inner = sel || placeholder;
      return { text: `${before}${inner}${after}`, caretOffset: before.length + inner.length };
    });
  }
  function prefixLines(prefix: string | ((i: number) => string), placeholder = "elemento") {
    applyToSelection((sel) => {
      const lines = (sel || placeholder).split("\n");
      const out = lines.map((l, i) => (typeof prefix === "function" ? prefix(i) : prefix) + l).join("\n");
      return { text: out };
    });
  }
  function setHeading(level: number) {
    if (level === 0) { applyToSelection((sel) => ({ text: (sel || "Párrafo").replace(/^#+\s*/, "") })); return; }
    const hashes = "#".repeat(level);
    applyToSelection((sel) => ({ text: `${hashes} ${(sel || "Título").replace(/^#+\s*/, "")}` }));
  }
  function insertLink() {
    const url = window.prompt("URL del enlace (https://…)");
    if (!url) return;
    applyToSelection((sel) => ({ text: `[${sel || "texto del enlace"}](${url.trim()})` }));
  }
  function insertColor() {
    const color = window.prompt("Color en HEX (ej: #7C3AED) o nombre CSS", "#7C3AED");
    if (!color) return;
    wrap(`<span style="color:${color.trim()}">`, `</span>`);
  }
  function insertPullquote() {
    applyToSelection((sel) => ({ text: `\n\n<aside class="pullquote">${sel || "Cita destacada"}</aside>\n\n` }));
  }
  function insertAlign(dir: "left" | "center" | "right" | "justify") {
    applyToSelection((sel) => ({ text: `\n\n<div style="text-align:${dir}">\n\n${sel || "texto"}\n\n</div>\n\n` }));
  }
  function insertHtmlBlock() {
    const html = window.prompt("Pega el HTML a insertar (iframe, embed, tabla, etc.)");
    if (!html) return;
    insertAtCursor(`\n\n${html}\n\n`);
  }
  async function onInlineImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.files?.[0];
    if (!raw) return;
    setInlineImgBusy(true);
    try {
      // Menor resolución para imágenes en línea (ligeras dentro del texto)
      const file = await optimizeImage(raw, { maxDim: 1000, quality: 0.78 });
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const path = `${userId}/noticias/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const { error } = await supabase.storage.from("media").upload(path, file, { contentType: file.type });
      if (error) throw error;
      const { data } = await supabase.storage.from("media").createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
      insertAtCursor(`\n\n![${raw.name}](${data?.signedUrl ?? ""})\n\n`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error subiendo imagen");
    } finally {
      setInlineImgBusy(false);
      if (inlineImgRef.current) inlineImgRef.current.value = "";
    }
  }


  async function load() {
    const { data } = await supabase.from("noticias").select("*").order("created_at", { ascending: false });
    setItems((data ?? []) as Noticia[]);
  }
  useEffect(() => {
    load();
  }, []);

  async function onCoverUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.files?.[0];
    if (!raw || !editing) return;
    setImgBusy(true);
    try {
      const file = await optimizeImage(raw, { maxDim: 1600, quality: 0.82 });
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const path = `${userId}/noticias/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const { error } = await supabase.storage.from("media").upload(path, file, { contentType: file.type });
      if (error) throw error;
      const { data } = await supabase.storage.from("media").createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
      setEditing({ ...editing, og_image_url: data?.signedUrl ?? "" });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error subiendo imagen");
    } finally {
      setImgBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function save() {
    if (!editing) return;
    setErr(null);
    setBusy(true);
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const slug = editing.slug || slugify(editing.title ?? "");
      const payload = {
        slug,
        title: editing.title ?? "",
        excerpt: editing.excerpt || null,
        body_md: editing.body_md || null,
        category: editing.category || null,
        is_published: !!editing.is_published,
        published_at: editing.is_published ? (editing.published_at ?? new Date().toISOString()) : null,
        seo_title: editing.seo_title || null,
        seo_description: editing.seo_description || null,
        og_image_url: editing.og_image_url || null,
        author_id: userId,
      };
      const q = editing.id
        ? supabase.from("noticias").update(payload).eq("id", editing.id)
        : supabase.from("noticias").insert(payload);
      const { error } = await q;
      if (error) throw error;
      setEditing(null);
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error");
    } finally {
      setBusy(false);
    }
  }

  async function remove(n: Noticia) {
    if (!confirm(`Eliminar "${n.title}"?`)) return;
    await supabase.from("noticias").delete().eq("id", n.id);
    load();
  }

  if (editing) {
    return (
      <div className="bg-white border border-[var(--color-outline-variant)] p-6 space-y-4 max-w-3xl">
        <Field label="Título">
          <input
            value={editing.title ?? ""}
            onChange={(e) =>
              setEditing({ ...editing, title: e.target.value, slug: editing.id ? editing.slug : slugify(e.target.value) })
            }
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
          />
        </Field>
        <Field label="Slug (URL)">
          <input
            value={editing.slug ?? ""}
            onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2 font-mono text-sm"
          />
        </Field>
        <Field label="Categoría">
          <input
            value={editing.category ?? ""}
            onChange={(e) => setEditing({ ...editing, category: e.target.value })}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
          />
        </Field>
        <Field label="Bajada (excerpt)">
          <textarea
            value={editing.excerpt ?? ""}
            onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
            rows={2}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
          />
        </Field>
        <Field label="Cuerpo">
          <div className="border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-low)]">
            <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b border-[var(--color-outline-variant)] bg-white text-sm">
              <select
                onChange={(e) => { setHeading(Number(e.target.value)); e.currentTarget.selectedIndex = 0; }}
                className="text-xs border border-[var(--color-outline-variant)] px-2 py-1 bg-white mr-1"
                defaultValue=""
                title="Estilo de párrafo / subtítulos"
              >
                <option value="" disabled>Párrafo ▾</option>
                <option value="0">Párrafo</option>
                <option value="2">Subtítulo H2</option>
                <option value="3">Subtítulo H3</option>
                <option value="4">Subtítulo H4</option>
              </select>
              <TbBtn onClick={() => wrap("**")} title="Negrita (Ctrl+B)"><b>B</b></TbBtn>
              <TbBtn onClick={() => wrap("*")} title="Cursiva (Ctrl+I)"><i>I</i></TbBtn>
              <TbBtn onClick={() => wrap("<u>", "</u>")} title="Subrayado"><u>U</u></TbBtn>
              <TbBtn onClick={() => wrap("~~")} title="Tachado"><s>S</s></TbBtn>
              <TbSep />
              <TbBtn onClick={insertColor} title="Color de texto"><span style={{ color: "#7C3AED" }}>A</span></TbBtn>
              <TbBtn onClick={() => wrap("<mark>", "</mark>")} title="Resaltar"><mark className="px-0.5">H</mark></TbBtn>
              <TbSep />
              <TbBtn onClick={insertLink} title="Enlace">🔗</TbBtn>
              <TbBtn onClick={() => inlineImgRef.current?.click()} disabled={inlineImgBusy} title="Insertar imagen (optimizada, baja resolución)">
                {inlineImgBusy ? "…" : "🖼"}
              </TbBtn>
              <input ref={inlineImgRef} type="file" accept="image/*" onChange={onInlineImageUpload} className="hidden" />
              <TbBtn onClick={insertHtmlBlock} title="Insertar HTML (iframe, embed…)">{"</>"}</TbBtn>
              <TbSep />
              <TbBtn onClick={() => prefixLines("- ")} title="Lista con viñetas">• ≡</TbBtn>
              <TbBtn onClick={() => prefixLines((i) => `${i + 1}. `)} title="Lista numerada">1. ≡</TbBtn>
              <TbBtn onClick={() => prefixLines("> ")} title="Cita">❝</TbBtn>
              <TbBtn onClick={insertPullquote} title="Destacar cuña / cita / párrafo">❞❞</TbBtn>
              <TbSep />
              <TbBtn onClick={() => insertAlign("left")} title="Alinear a la izquierda">⇤</TbBtn>
              <TbBtn onClick={() => insertAlign("center")} title="Centrar">≡</TbBtn>
              <TbBtn onClick={() => insertAlign("right")} title="Alinear a la derecha">⇥</TbBtn>
              <TbBtn onClick={() => insertAlign("justify")} title="Justificar">☰</TbBtn>
              <TbSep />
              <TbBtn onClick={() => insertAtCursor("\n\n---\n\n")} title="Línea horizontal">―</TbBtn>
            </div>
            <textarea
              ref={bodyRef}
              value={editing.body_md ?? ""}
              onChange={(e) => setEditing({ ...editing, body_md: e.target.value })}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") { e.preventDefault(); wrap("**"); }
                if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "i") { e.preventDefault(); wrap("*"); }
                if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); insertLink(); }
              }}
              rows={16}
              className="w-full px-3 py-2 font-mono text-sm bg-white border-0 focus:outline-none focus:ring-0 resize-y"
            />
          </div>
          <p className="text-[11px] text-[var(--color-on-surface-variant)] mt-1">
            Selecciona texto y aplica formato con la barra. Las imágenes se optimizan automáticamente (baja resolución para carga rápida). Atajos: <kbd>Ctrl+B</kbd> / <kbd>Ctrl+I</kbd> / <kbd>Ctrl+K</kbd>.
          </p>
        </Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="SEO title">
            <input
              value={editing.seo_title ?? ""}
              onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })}
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
            />
          </Field>
          <Field label="Imagen (1200×630 · 16:9)">
            <div className="space-y-2">
              {editing.og_image_url && (
                <div className="aspect-[1200/630] bg-[var(--color-surface-container-low)] overflow-hidden max-w-sm">
                  <img src={editing.og_image_url} alt="preview" className="w-full h-full object-cover" />
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={onCoverUpload} disabled={imgBusy} className="text-sm" />
              {imgBusy && <p className="text-xs">Subiendo…</p>}
              <input
                value={editing.og_image_url ?? ""}
                onChange={(e) => setEditing({ ...editing, og_image_url: e.target.value })}
                placeholder="…o pega una URL"
                className="w-full border border-[var(--color-outline-variant)] px-3 py-2 text-xs font-mono"
              />
            </div>
          </Field>
        </div>
        <Field label="SEO description">
          <textarea
            value={editing.seo_description ?? ""}
            onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })}
            rows={2}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
          />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!editing.is_published}
            onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })}
          />
          Publicar en la web
        </label>
        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex gap-2">
          <button onClick={save} disabled={busy} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold disabled:opacity-50">
            {busy ? "Guardando..." : "Guardar"}
          </button>
          <button onClick={() => setEditing(null)} className="px-4 py-2 border border-[var(--color-outline-variant)]">
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (

    <ListShell
      title={`Noticias (${items.length})`}
      onNew={() => setEditing({ is_published: false })}
      newLabel="+ Nueva noticia"
      empty="Sin noticias todavía."
      rows={items.map((n) => ({
        key: n.id,
        title: n.title,
        meta: `/${n.slug} · ${n.is_published ? "PUBLICADA" : "Borrador"} · ${new Date(n.created_at).toLocaleDateString()} · ${fmtAuthor(lookup, n.author_id, n.updated_by)}`,
        onEdit: () => setEditing(n),
        onDelete: () => remove(n),
      }))}
    />
  );
}

/* ---------------- NEWSLETTER ---------------- */

type Newsletter = {
  id: string;
  subject: string;
  body_html: string | null;
  status: string;
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string;
  created_by: string | null;
  updated_by: string | null;
};

function NewsletterPanel() {
  const [items, setItems] = useState<Newsletter[]>([]);
  const [editing, setEditing] = useState<Partial<Newsletter> | null>(null);
  const [preview, setPreview] = useState<"desktop" | "tablet" | "mobile">("desktop");
  const lookup = useActorEmails(items.flatMap((n) => [n.created_by, n.updated_by]));


  async function load() {
    const { data } = await supabase.from("newsletters").select("*").order("created_at", { ascending: false });
    setItems((data ?? []) as Newsletter[]);
  }
  useEffect(() => {
    load();
  }, []);

  async function save() {
    if (!editing) return;
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const payload = {
      subject: editing.subject ?? "",
      body_html: editing.body_html ?? "",
      status: editing.status ?? "draft",
      sent_at: editing.status === "sent" ? new Date().toISOString() : null,
      created_by: userId,
    };
    const q = editing.id
      ? supabase.from("newsletters").update(payload).eq("id", editing.id)
      : supabase.from("newsletters").insert(payload);
    const { error } = await q;
    if (error) return alert(error.message);
    setEditing(null);
    load();
  }

  async function remove(n: Newsletter) {
    if (!confirm(`Eliminar "${n.subject}"?`)) return;
    await supabase.from("newsletters").delete().eq("id", n.id);
    load();
  }

  if (editing) {
    const width = preview === "desktop" ? 720 : preview === "tablet" ? 500 : 340;
    return (
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[var(--color-outline-variant)] p-6 space-y-4">
          <Field label="Asunto">
            <input
              value={editing.subject ?? ""}
              onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
            />
          </Field>
          <Field label="HTML (pega el código exportado desde HubSpot)">
            <textarea
              value={editing.body_html ?? ""}
              onChange={(e) => setEditing({ ...editing, body_html: e.target.value })}
              rows={20}
              placeholder="<html>…</html>"
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2 font-mono text-xs"
            />
          </Field>
          <Field label="Estado">
            <select
              value={editing.status ?? "draft"}
              onChange={(e) => setEditing({ ...editing, status: e.target.value })}
              className="border border-[var(--color-outline-variant)] px-3 py-2"
            >
              <option value="draft">Borrador (no visible en web)</option>
              <option value="sent">Enviada (visible en web)</option>
            </select>
          </Field>
          <div className="flex gap-2">
            <button onClick={save} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold">
              Guardar
            </button>
            <button onClick={() => setEditing(null)} className="px-4 py-2 border border-[var(--color-outline-variant)]">
              Cancelar
            </button>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex gap-1 bg-[var(--color-surface-container-low)] p-1 w-fit">
            {(["desktop", "tablet", "mobile"] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPreview(p)}
                className={`px-3 py-1.5 text-xs font-semibold ${
                  preview === p ? "bg-white text-[var(--color-brand-purple)]" : "text-[var(--color-on-surface-variant)]"
                }`}
              >
                {p === "desktop" ? "💻 PC" : p === "tablet" ? "📱 Tablet" : "📱 Móvil"}
              </button>
            ))}
          </div>
          <div className="border border-[var(--color-outline-variant)] bg-white overflow-auto flex justify-center p-4">
            <iframe
              title="Preview newsletter"
              srcDoc={editing.body_html ?? "<p style='font-family:sans-serif;padding:2rem;color:#999'>Pega el HTML para previsualizar</p>"}
              style={{ width, minHeight: 600, border: "1px solid #e5e5e5" }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (

    <ListShell
      title={`Newsletters (${items.length})`}
      onNew={() => setEditing({ status: "draft", body_html: "" })}
      newLabel="+ Nueva newsletter"
      empty="Sin newsletters."
      rows={items.map((n) => ({
        key: n.id,
        title: n.subject,
        meta: `${n.status.toUpperCase()} · ${new Date(n.created_at).toLocaleDateString()} · ${fmtAuthor(lookup, n.created_by, n.updated_by)}`,
        onEdit: () => setEditing(n),
        onDelete: () => remove(n),
      }))}
    />
  );
}

/* ---------------- MULTIMEDIA (folders + assets) ---------------- */

type Folder = { id: string; name: string; parent_id: string | null };
type Asset = {
  id: string;
  folder_id: string | null;
  storage_path: string;
  file_name: string;
  mime_type: string | null;
  file_size_bytes: number | null;
  uploaded_by: string | null;
  updated_by: string | null;
};
const BUCKET = "media";

function guessMime(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  const map: Record<string, string> = {
    jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", gif: "image/gif",
    webp: "image/webp", avif: "image/avif", svg: "image/svg+xml", bmp: "image/bmp",
    heic: "image/heic", heif: "image/heif",
    mp4: "video/mp4", mov: "video/quicktime", webm: "video/webm", m4v: "video/mp4",
    mkv: "video/x-matroska", avi: "video/x-msvideo",
  };
  return map[ext] ?? "application/octet-stream";
}

function assetKind(a: { file_name: string; mime_type: string | null }): "image" | "video" | "other" {
  const mime = a.mime_type || guessMime(a.file_name);
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  return "other";
}

const ALLOWED_IMAGE_EXT = ["jpg", "jpeg", "png", "gif", "webp", "avif", "svg"];
const ALLOWED_VIDEO_EXT = ["mp4", "mov", "webm", "m4v"];
const ALLOWED_EXT = [...ALLOWED_IMAGE_EXT, ...ALLOWED_VIDEO_EXT];
const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_VIDEO_BYTES = 200 * 1024 * 1024; // 200 MB
const ACCEPT_ATTR = ALLOWED_EXT.map((e) => `.${e}`).join(",");

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function validateFile(file: File): string | null {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (!ALLOWED_EXT.includes(ext)) {
    return `formato .${ext || "?"} no permitido (usa ${ALLOWED_EXT.join(", ")})`;
  }
  const isVideo = ALLOWED_VIDEO_EXT.includes(ext);
  const max = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
  if (file.size > max) {
    return `${formatBytes(file.size)} supera el límite de ${formatBytes(max)}`;
  }
  if (file.size === 0) return "archivo vacío";
  return null;
}

function MultimediaPanel() {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [signed, setSigned] = useState<Record<string, string>>({});
  const [current, setCurrent] = useState<string | null>(null);
  const [newFolder, setNewFolder] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const fileRef = useRef<HTMLInputElement>(null);

  const currentFolder = useMemo(() => folders.find((f) => f.id === current) ?? null, [folders, current]);
  const breadcrumb = useMemo(() => {
    const chain: Folder[] = [];
    let node = currentFolder;
    while (node) {
      chain.unshift(node);
      node = folders.find((f) => f.id === node!.parent_id) ?? null;
    }
    return chain;
  }, [currentFolder, folders]);

  const childFolders = folders.filter((f) => f.parent_id === current);
  const childAssets = assets.filter((a) => a.folder_id === current);

  async function load() {
    const [f, a] = await Promise.all([
      supabase.from("media_folders").select("id,name,parent_id").order("name"),
      supabase
        .from("media_assets")
        .select("id,folder_id,storage_path,file_name,mime_type,file_size_bytes,uploaded_by,updated_by")
        .order("created_at", { ascending: false })
        .limit(500),
    ]);
    setFolders((f.data ?? []) as Folder[]);
    const list = (a.data ?? []) as Asset[];
    setAssets(list);
    // Batch sign URLs — más rápido y confiable que N firmas paralelas (que a veces fallan)
    const map: Record<string, string> = {};
    const CHUNK = 100;
    for (let i = 0; i < list.length; i += CHUNK) {
      const slice = list.slice(i, i + CHUNK);
      const { data: signedList } = await supabase.storage
        .from(BUCKET)
        .createSignedUrls(slice.map((x) => x.storage_path), 3600);
      signedList?.forEach((s, idx) => {
        const asset = slice[idx];
        if (s?.signedUrl && asset) map[asset.id] = s.signedUrl;
      });
    }
    setSigned(map);
  }
  useEffect(() => {
    load();
  }, []);

  async function createFolder() {
    const name = newFolder.trim();
    if (!name) return;
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const { error } = await supabase.from("media_folders").insert({ name, parent_id: current, created_by: userId });
    if (error) return setErr(error.message);
    setNewFolder("");
    load();
  }

  async function deleteFolder(f: Folder) {
    if (!confirm(`Eliminar carpeta "${f.name}"?`)) return;
    await supabase.from("media_folders").delete().eq("id", f.id);
    load();
  }

  async function renameFolder(f: Folder) {
    const name = prompt("Nuevo nombre de la carpeta:", f.name)?.trim();
    if (!name || name === f.name) return;
    const { error } = await supabase.from("media_folders").update({ name }).eq("id", f.id);
    if (error) {
      setErr(error.message);
      return;
    }
    load();
  }

  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  async function uploadFiles(files: File[]) {
    if (files.length === 0) return;
    setErr(null);
    setFileErrors([]);
    const errors: string[] = [];
    const valid: File[] = [];
    for (const f of files) {
      const v = validateFile(f);
      if (v) errors.push(`${f.name}: ${v}`);
      else valid.push(f);
    }
    if (valid.length === 0) {
      setFileErrors(errors);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setBusy(true);
    setUploadProgress({ done: 0, total: valid.length });
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const folderSlug = current ? `folder-${current}` : "root";
      for (let i = 0; i < valid.length; i++) {
        let file = valid[i];
        try {
          if (file.type.startsWith("image/")) {
            file = await optimizeImage(file, { maxDim: 2000, quality: 0.82 });
          }
          const mime = file.type || guessMime(file.name);
          const path = `${userId}/${folderSlug}/${Date.now()}-${i}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
          const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, { contentType: mime });
          if (upErr) throw upErr;
          const { error: insErr } = await supabase.from("media_assets").insert({
            folder_id: current,
            storage_path: path,
            file_name: file.name,
            mime_type: mime,
            file_size_bytes: file.size,
            uploaded_by: userId,
          });
          if (insErr) throw insErr;
        } catch (fileErr) {
          errors.push(`${file.name}: ${fileErr instanceof Error ? fileErr.message : "error"}`);
        }
        setUploadProgress({ done: i + 1, total: valid.length });
      }
      if (fileRef.current) fileRef.current.value = "";
      if (errors.length) setFileErrors(errors);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error subiendo archivos");
    } finally {
      setBusy(false);
      setUploadProgress(null);
    }
  }

  async function onUpload(e: React.ChangeEvent<HTMLInputElement>) {
    await uploadFiles(Array.from(e.target.files ?? []));
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length) uploadFiles(files);
  }



  async function remove(a: Asset) {
    if (!confirm(`Eliminar ${a.file_name}?`)) return;
    await supabase.storage.from(BUCKET).remove([a.storage_path]);
    await supabase.from("media_assets").delete().eq("id", a.id);
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(a.id);
      return next;
    });
    load();
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    setSelected(new Set(childAssets.map((a) => a.id)));
  }

  function clearSelection() {
    setSelected(new Set());
  }

  async function removeSelected() {
    if (selected.size === 0) return;
    const names = childAssets.filter((a) => selected.has(a.id)).map((a) => a.file_name);
    if (!confirm(`Eliminar ${selected.size} archivo(s)?\n${names.slice(0, 5).join("\n")}${names.length > 5 ? "\n…" : ""}`)) return;
    setBusy(true);
    try {
      const toDelete = childAssets.filter((a) => selected.has(a.id));
      await supabase.storage.from(BUCKET).remove(toDelete.map((a) => a.storage_path));
      await supabase.from("media_assets").delete().in("id", Array.from(selected));
      setSelected(new Set());
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error eliminando archivos");
    } finally {
      setBusy(false);
    }
  }

  const lookup = useActorEmails(assets.flatMap((a) => [a.uploaded_by, a.updated_by]));
  return (
    <div className="space-y-4">
      <div className="text-sm flex flex-wrap items-center gap-1 text-[var(--color-on-surface-variant)]">
        <button onClick={() => setCurrent(null)} className="hover:underline font-semibold text-[var(--color-brand-purple)]">
          📁 Raíz
        </button>
        {breadcrumb.map((f) => (
          <span key={f.id} className="flex items-center gap-1">
            <span>/</span>
            <button onClick={() => setCurrent(f.id)} className="hover:underline">
              {f.name}
            </button>
          </span>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-[var(--color-outline-variant)] p-4">
          <h3 className="font-bold text-sm mb-2">Nueva carpeta {currentFolder ? `en "${currentFolder.name}"` : ""}</h3>
          <div className="flex gap-2">
            <input
              placeholder="Ej: Fotos Congreso 2026"
              value={newFolder}
              onChange={(e) => setNewFolder(e.target.value)}
              className="flex-1 border border-[var(--color-outline-variant)] px-3 py-2 text-sm"
            />
            <button onClick={createFolder} className="px-4 py-2 bg-[var(--color-brand-purple)] text-white text-sm font-bold">
              Crear
            </button>
          </div>
        </div>
        <div
          className={`bg-white border-2 border-dashed p-4 transition-colors ${
            isDragging ? "border-[var(--color-brand-purple)] bg-purple-50" : "border-[var(--color-outline-variant)]"
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
        >
          <h3 className="font-bold text-sm mb-2">Subir fotos o videos {currentFolder ? `a "${currentFolder.name}"` : ""}</h3>
          <input ref={fileRef} type="file" accept={ACCEPT_ATTR} multiple onChange={onUpload} disabled={busy} className="text-sm" />
          <p className="text-xs mt-1 text-[var(--color-on-surface-variant)]">
            Puedes seleccionar varios archivos, o <strong>arrastrarlos aquí</strong>.
          </p>
          <p className="text-xs mt-1 text-[var(--color-on-surface-variant)]">
            Formatos: {ALLOWED_IMAGE_EXT.join(", ")} (máx {formatBytes(MAX_IMAGE_BYTES)}) · {ALLOWED_VIDEO_EXT.join(", ")} (máx {formatBytes(MAX_VIDEO_BYTES)}).
          </p>
          <p className="text-[11px] mt-1 text-[var(--color-on-surface-variant)] italic">
            Las imágenes se optimizan automáticamente (WebP, máx 2000px) al subirlas.
          </p>
          {busy && uploadProgress && (
            <p className="text-xs mt-2">Subiendo {uploadProgress.done} de {uploadProgress.total}…</p>
          )}
        </div>
      </div>
      {err && <p className="text-sm text-red-600">{err}</p>}
      {fileErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          <div className="flex items-center justify-between mb-1">
            <strong>Archivos con problemas ({fileErrors.length}):</strong>
            <button onClick={() => setFileErrors([])} className="text-xs underline">Cerrar</button>
          </div>
          <ul className="list-disc pl-5 space-y-0.5">
            {fileErrors.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}

      {childFolders.length > 0 && (
        <div>
          <h3 className="text-xs uppercase tracking-widest text-[var(--color-on-surface-variant)] mb-2">Carpetas</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {childFolders.map((f) => (
              <div key={f.id} className="bg-white border border-[var(--color-outline-variant)] p-4 flex items-center justify-between gap-2 group">
                <button onClick={() => setCurrent(f.id)} className="flex items-center gap-2 text-left flex-1 truncate">
                  <span className="text-2xl">📁</span>
                  <span className="font-semibold truncate">{f.name}</span>
                </button>
                <button
                  onClick={() => renameFolder(f)}
                  title="Renombrar"
                  className="text-xs text-[var(--color-brand-purple)] opacity-0 group-hover:opacity-100"
                >
                  ✎
                </button>
                <button onClick={() => deleteFolder(f)} title="Eliminar" className="text-xs text-red-600 opacity-0 group-hover:opacity-100">
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h3 className="text-xs uppercase tracking-widest text-[var(--color-on-surface-variant)]">
            Archivos ({childAssets.length})
          </h3>
          {childAssets.length > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-[var(--color-on-surface-variant)]">{selected.size} seleccionado(s)</span>
              {selected.size === 0 ? (
                <button onClick={selectAll} className="underline text-[var(--color-brand-purple)]">
                  Seleccionar todos
                </button>
              ) : (
                <>
                  <button onClick={clearSelection} className="underline text-[var(--color-on-surface-variant)]">
                    Limpiar
                  </button>
                  <button
                    onClick={removeSelected}
                    disabled={busy}
                    className="px-3 py-1 bg-red-600 text-white font-semibold disabled:opacity-50"
                  >
                    Eliminar {selected.size}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {childAssets.map((a) => {
            const isSelected = selected.has(a.id);
            return (
              <div
                key={a.id}
                onClick={() => toggleSelect(a.id)}
                className={`bg-white border overflow-hidden cursor-pointer transition-shadow ${
                  isSelected ? "border-[var(--color-brand-purple)] ring-2 ring-[var(--color-brand-purple)]" : "border-[var(--color-outline-variant)]"
                }`}
              >
                <div className="aspect-square bg-[var(--color-surface-container-low)] flex items-center justify-center overflow-hidden relative">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(a.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-2 left-2 w-4 h-4 z-10"
                    aria-label={`Seleccionar ${a.file_name}`}
                  />
                  {signed[a.id] && assetKind(a) === "image" ? (
                    <img
                      src={signed[a.id]}
                      alt={a.file_name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : signed[a.id] && assetKind(a) === "video" ? (
                    <video src={signed[a.id]} className="w-full h-full object-cover" muted preload="metadata" />
                  ) : (
                    <span className="text-3xl">📄</span>
                  )}
                </div>
                <div className="p-3">
                  <div className="text-xs font-mono truncate" title={a.file_name}>
                    {a.file_name}
                  </div>
                  <div className="text-[10px] text-[var(--color-on-surface-variant)] truncate">
                    {fmtAuthor(lookup, a.uploaded_by, a.updated_by)}
                  </div>
                  <div className="flex gap-2 mt-2 text-xs">
                    {signed[a.id] && (
                      <a
                        href={signed[a.id]}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="underline text-[var(--color-brand-purple)]"
                      >
                        Ver
                      </a>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(a);
                      }}
                      className="text-red-600 ml-auto"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {childAssets.length === 0 && childFolders.length === 0 && (
            <div className="col-span-full text-center py-12 text-[var(--color-on-surface-variant)]">
              Carpeta vacía. Crea subcarpetas o sube archivos.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- PODCAST ---------------- */

type Episode = {
  id: string;
  title: string;
  subtitle: string | null;
  episode_number: number | null;
  description: string | null;
  spotify_embed_html: string | null;
  youtube_embed_html: string | null;
  cover_image_url: string | null;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
  created_by: string | null;
  updated_by: string | null;
};

function PodcastPanel() {
  const [items, setItems] = useState<Episode[]>([]);
  const [editing, setEditing] = useState<Partial<Episode> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [imgBusy, setImgBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const lookup = useActorEmails(items.flatMap((n) => [n.created_by, n.updated_by]));

  async function load() {

    const { data } = await supabase
      .from("podcast_episodes" as never)
      .select("*")
      .order("created_at", { ascending: false });
    setItems((data ?? []) as Episode[]);
  }
  useEffect(() => {
    load();
  }, []);

  async function onCoverUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.files?.[0];
    if (!raw || !editing) return;
    setImgBusy(true);
    try {
      const file = await optimizeImage(raw, { maxDim: 1600, quality: 0.82 });
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const path = `${userId}/podcast/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const { error } = await supabase.storage.from("media").upload(path, file, { contentType: file.type });
      if (error) throw error;
      const { data } = await supabase.storage.from("media").createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
      setEditing({ ...editing, cover_image_url: data?.signedUrl ?? "" });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error");
    } finally {
      setImgBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function save() {
    if (!editing) return;
    const payload = {
      title: editing.title ?? "",
      subtitle: editing.subtitle || null,
      episode_number: editing.episode_number ?? null,
      description: editing.description || null,
      spotify_embed_html: editing.spotify_embed_html || null,
      youtube_embed_html: editing.youtube_embed_html || null,
      cover_image_url: editing.cover_image_url || null,
      is_published: !!editing.is_published,
      published_at: editing.is_published ? (editing.published_at ?? new Date().toISOString()) : null,
    };
    const q = editing.id
      ? supabase
          .from("podcast_episodes" as never)
          .update(payload as never)
          .eq("id", editing.id)
      : supabase.from("podcast_episodes" as never).insert(payload as never);
    const { error } = await q;
    if (error) return setErr(error.message);
    setEditing(null);
    load();
  }

  async function remove(n: Episode) {
    if (!confirm(`Eliminar "${n.title}"?`)) return;
    await supabase.from("podcast_episodes" as never).delete().eq("id", n.id);
    load();
  }

  if (editing) {
    return (
      <div className="bg-white border border-[var(--color-outline-variant)] p-6 space-y-4 max-w-3xl">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Título">
            <input
              value={editing.title ?? ""}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
            />
          </Field>
          <Field label="Número de episodio">
            <input
              type="number"
              value={editing.episode_number ?? ""}
              onChange={(e) => setEditing({ ...editing, episode_number: e.target.value ? Number(e.target.value) : null })}
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
            />
          </Field>
        </div>
        <Field label="Bajada">
          <input
            value={editing.subtitle ?? ""}
            onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
          />
        </Field>
        <Field label="Descripción">
          <textarea
            value={editing.description ?? ""}
            onChange={(e) => setEditing({ ...editing, description: e.target.value })}
            rows={4}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
          />
        </Field>
        <Field label="Imagen de portada">
          <div className="space-y-2">
            {editing.cover_image_url && (
              <img src={editing.cover_image_url} alt="" className="max-w-xs aspect-square object-cover" />
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={onCoverUpload} disabled={imgBusy} className="text-sm" />
          </div>
        </Field>
        <Field label="Embed Spotify (HTML iframe)">
          <textarea
            value={editing.spotify_embed_html ?? ""}
            onChange={(e) => setEditing({ ...editing, spotify_embed_html: e.target.value })}
            rows={3}
            placeholder='<iframe src="https://open.spotify.com/embed/episode/..."></iframe>'
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2 font-mono text-xs"
          />
        </Field>
        <Field label="Embed YouTube (HTML iframe)">
          <textarea
            value={editing.youtube_embed_html ?? ""}
            onChange={(e) => setEditing({ ...editing, youtube_embed_html: e.target.value })}
            rows={3}
            placeholder='<iframe src="https://youtube.com/embed/..."></iframe>'
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2 font-mono text-xs"
          />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!editing.is_published}
            onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })}
          />
          Publicar
        </label>
        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex gap-2">
          <button onClick={save} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold">
            Guardar
          </button>
          <button onClick={() => setEditing(null)} className="px-4 py-2 border border-[var(--color-outline-variant)]">
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <ListShell

      title={`Episodios (${items.length})`}
      onNew={() => setEditing({ is_published: false })}
      newLabel="+ Nuevo episodio"
      empty="Sin episodios."
      rows={items.map((n) => ({
        key: n.id,
        title: `${n.episode_number ? `#${n.episode_number} · ` : ""}${n.title}`,
        meta: `${n.is_published ? "PUBLICADO" : "Borrador"} · ${new Date(n.created_at).toLocaleDateString()} · ${fmtAuthor(lookup, n.created_by, n.updated_by)}`,
        onEdit: () => setEditing(n),
        onDelete: () => remove(n),
      }))}
    />
  );
}

/* ---------------- DOCUMENTOS ---------------- */

type Documento = {
  id: string;
  title: string;
  area: string | null;
  description: string | null;
  file_url: string;
  file_name: string | null;
  is_published: boolean;
  created_at: string;
  created_by: string | null;
  updated_by: string | null;
};

function DocumentosPanel() {
  const [items, setItems] = useState<Documento[]>([]);
  const [editing, setEditing] = useState<Partial<Documento> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const lookup = useActorEmails(items.flatMap((n) => [n.created_by, n.updated_by]));


  async function load() {
    const { data } = await supabase.from("documentos" as never).select("*").order("created_at", { ascending: false });
    setItems((data ?? []) as Documento[]);
  }
  useEffect(() => {
    load();
  }, []);

  async function onFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    setBusy(true);
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const path = `${userId}/documentos/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const { error } = await supabase.storage.from("media").upload(path, file, { contentType: file.type });
      if (error) throw error;
      const { data } = await supabase.storage.from("media").createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
      setEditing({ ...editing, file_url: data?.signedUrl ?? "", file_name: editing.file_name || file.name });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function save() {
    if (!editing) return;
    if (!editing.file_url) return setErr("Sube un archivo primero.");
    const payload = {
      title: editing.title ?? "",
      area: editing.area || null,
      description: editing.description || null,
      file_url: editing.file_url,
      file_name: editing.file_name || null,
      is_published: editing.is_published ?? true,
    };
    const q = editing.id
      ? supabase
          .from("documentos" as never)
          .update(payload as never)
          .eq("id", editing.id)
      : supabase.from("documentos" as never).insert(payload as never);
    const { error } = await q;
    if (error) return setErr(error.message);
    setEditing(null);
    load();
  }

  async function remove(n: Documento) {
    if (!confirm(`Eliminar "${n.title}"?`)) return;
    await supabase.from("documentos" as never).delete().eq("id", n.id);
    load();
  }


  if (editing) {
    return (
      <div className="bg-white border border-[var(--color-outline-variant)] p-6 space-y-4 max-w-2xl">
        <Field label="Título">
          <input
            value={editing.title ?? ""}
            onChange={(e) => setEditing({ ...editing, title: e.target.value })}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
          />
        </Field>
        <Field label="Área / Categoría">
          <input
            value={editing.area ?? ""}
            onChange={(e) => setEditing({ ...editing, area: e.target.value })}
            placeholder="Ej: Radiografía, Memoria, Reporte técnico"
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
          />
        </Field>
        <Field label="Descripción">
          <textarea
            value={editing.description ?? ""}
            onChange={(e) => setEditing({ ...editing, description: e.target.value })}
            rows={3}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
          />
        </Field>
        <Field label="Archivo (PDF u otro)">
          <div className="space-y-2">
            <input ref={fileRef} type="file" onChange={onFileUpload} disabled={busy} className="text-sm" />
            {busy && <p className="text-xs">Subiendo…</p>}
            {editing.file_url && <p className="text-xs text-green-700">✓ {editing.file_name}</p>}
          </div>
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={editing.is_published !== false}
            onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })}
          />
          Publicar
        </label>
        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex gap-2">
          <button onClick={save} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold">
            Guardar
          </button>
          <button onClick={() => setEditing(null)} className="px-4 py-2 border border-[var(--color-outline-variant)]">
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (

    <ListShell
      title={`Documentos (${items.length})`}
      onNew={() => setEditing({ is_published: true })}
      newLabel="+ Nuevo documento"
      empty="Sin documentos."
      rows={items.map((n) => ({
        key: n.id,
        title: n.title,
        meta: `${n.area ?? "Sin área"} · ${new Date(n.created_at).toLocaleDateString()} · ${fmtAuthor(lookup, n.created_by, n.updated_by)}`,
        onEdit: () => setEditing(n),
        onDelete: () => remove(n),
      }))}
    />
  );
}

/* ---------------- Shared helpers ---------------- */

function TbBtn({ onClick, disabled, title, children }: { onClick: () => void; disabled?: boolean; title: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="min-w-[30px] h-[30px] px-2 text-sm border border-transparent hover:border-[var(--color-outline-variant)] hover:bg-[var(--color-surface-container-low)] disabled:opacity-40 flex items-center justify-center leading-none"
    >
      {children}
    </button>
  );
}

function TbSep() {
  return <span className="mx-1 w-px h-5 bg-[var(--color-outline-variant)]" aria-hidden />;
}


function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-on-surface-variant)] mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}

type Row = { key: string; title: string; meta: string; onEdit: () => void; onDelete: () => void };

function ListShell({
  title,
  onNew,
  newLabel,
  empty,
  rows,
}: {
  title: string;
  onNew: () => void;
  newLabel: string;
  empty: string;
  rows: Row[];
}) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-bold">{title}</h3>
        <button onClick={onNew} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold text-sm">
          {newLabel}
        </button>
      </div>
      <div className="bg-white border border-[var(--color-outline-variant)]">
        {rows.length === 0 ? (
          <p className="p-8 text-center text-[var(--color-on-surface-variant)]">{empty}</p>
        ) : (
          <ul className="divide-y divide-[var(--color-outline-variant)]">
            {rows.map((r) => (
              <li key={r.key} className="flex items-center justify-between p-4">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{r.title}</div>
                  <div className="text-xs text-[var(--color-on-surface-variant)]">{r.meta}</div>
                </div>
                <div className="flex gap-3 text-xs ml-4 shrink-0">
                  <button onClick={r.onEdit} className="underline">
                    Editar
                  </button>
                  <button onClick={r.onDelete} className="text-red-600">
                    Eliminar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
